// Ubicación en el repo: src/pages/PatientDetail.jsx
// Ficha de detalle de paciente (spec-pacientes-agenda.md 1.4, punto 4 del
// orden sugerido): datos generales, notas, últimas citas, resumen del
// historial clínico (audiometrías/recetas/audífonos/lentes) y documentos
// adjuntos (ver DECISIONS.md #20 y #37 — el bucket de Storage ya existía
// desde la migración inicial, solo faltaba esta pantalla).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { getPatientDetail } from '../lib/patients';
import { STATUS_COLORS } from '../lib/appointments';
import {
  formatPatientName,
  calculateAge,
  formatDate,
  formatCurrency,
  formatProfessionalName,
} from '../lib/format';
import PatientFormModal from '../components/PatientFormModal';
import PatientAttachments from '../components/PatientAttachments';
import ExamDetailModal from '../components/ExamDetailModal';
import PrescriptionDetailModal from '../components/PrescriptionDetailModal';
import { printConsentDocument } from '../lib/consentDocument';
import { printPrescriptionReport } from '../lib/prescriptionReport';
import { getPatientBranchLogoUrl } from '../lib/branchLogos';
import { getProfessionalSignatureUrl } from '../lib/professionalSignatures';
import Spinner from '../components/Spinner';

// Mismos topes que usa getPatientDetail() (src/lib/patients.js) — se
// repiten acá solo para decidir cuándo mostrar el aviso "mostrando los
// últimos N" (si la lista devuelta viene exactamente en el tope, es
// probable que haya más historial no mostrado).
const APPOINTMENTS_LIMIT = 5;
const HISTORY_LIMIT = 10;

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { professional, selectableBranches, activeBranchId } = useOutletContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [detailExam, setDetailExam] = useState(null);
  const [detailPrescription, setDetailPrescription] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const detail = await getPatientDetail(id);
      setData(detail);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la ficha del paciente.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading)
    return (
      <div className="app-loading">
        <Spinner size="lg" />
      </div>
    );
  if (error) return <p className="auth-error">{error}</p>;
  if (!data) return null;

  const { patient, branchNames, appointments, exams, prescriptions, hearingaids, oftsales } = data;
  const initials = [patient.nombre, patient.apellido]
    .map((s) => s?.trim()?.[0])
    .filter(Boolean)
    .join('')
    .toUpperCase();

  return (
    <div className="patient-detail">
      <button type="button" className="patient-detail__back" onClick={() => navigate('/app/pacientes')}>
        <span className="patient-detail__back-arrow">←</span> Volver a Pacientes
      </button>

      <div className="patient-detail__header">
        <div className="patient-detail__header-main">
          <div className="patient-detail__avatar" aria-hidden="true">
            {initials || '?'}
          </div>
          <div>
            <h1>{formatPatientName(patient)}</h1>
            <p className="text-muted">
              {patient.rut || 'Sin RUT'} · {calculateAge(patient.birthdate) ?? '—'} años · Ingreso:{' '}
              {formatDate(patient.created_at)}
            </p>
            <p className="text-muted">Sucursal(es): {branchNames.join(', ') || 'Sin asignar'}</p>
          </div>
        </div>
        <button type="button" className="button-primary" onClick={() => setEditing(true)}>
          Editar
        </button>
      </div>

      <section className="detail-section">
        <h2>
          <span className="detail-section__icon" aria-hidden="true">📋</span> Datos generales
        </h2>
        <dl className="detail-grid">
          <dt>Teléfono</dt>
          <dd>{patient.phone || '—'}</dd>
          <dt>Email</dt>
          <dd>{patient.email || '—'}</dd>
          <dt>Sexo</dt>
          <dd>{patient.sexo || '—'}</dd>
          <dt>Previsión</dt>
          <dd>
            {patient.prevision || '—'}
            {patient.prevision === 'Isapre' && patient.prevision_detalle
              ? ` (${patient.prevision_detalle})`
              : ''}
          </dd>
          <dt>Dirección</dt>
          <dd>{patient.address || '—'}</dd>
          <dt>Contacto de emergencia</dt>
          <dd>
            {patient.emergency_contact_name || '—'}
            {patient.emergency_contact_phone ? ` · ${patient.emergency_contact_phone}` : ''}
          </dd>
          <dt>Consentimiento informado</dt>
          <dd>
            {patient.consent_given
              ? `Firmado (${formatDate(patient.consent_date)})`
              : 'No firmado todavía'}{' '}
            <button
              type="button"
              className="detail-ghost-button"
              onClick={() => printConsentDocument(patient, branchNames.join(', '))}
            >
              🖨️ Generar e imprimir documento
            </button>
          </dd>
        </dl>
      </section>

      <section className="detail-section">
        <h2>
          <span className="detail-section__icon" aria-hidden="true">📝</span> Notas
        </h2>
        <p>{patient.notes || 'Sin notas registradas.'}</p>
      </section>

      <section className="detail-section">
        <h2>
          <span className="detail-section__icon" aria-hidden="true">📅</span> Últimas citas
        </h2>
        {appointments.length === 0 ? (
          <p className="detail-empty">Sin citas registradas.</p>
        ) : (
          <>
            <table className="detail-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Módulo</th>
                  <th>Profesional</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => (
                  <tr key={a.id}>
                    <td>
                      {formatDate(a.date)} {a.time?.slice(0, 5)}
                    </td>
                    <td>{a.type}</td>
                    <td>{a.module}</td>
                    <td>{formatProfessionalName(a.professionals)}</td>
                    <td>
                      <span className="dot" style={{ background: STATUS_COLORS[a.status] }} /> {a.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {appointments.length === APPOINTMENTS_LIMIT && (
              <p className="text-muted" style={{ marginTop: 6 }}>
                Mostrando las últimas {APPOINTMENTS_LIMIT} — puede haber más en Agenda.
              </p>
            )}
          </>
        )}
      </section>

      <div className="detail-columns">
        <section className="detail-section">
          <h2>
            <span className="detail-section__icon" aria-hidden="true">👂</span> Audiometrías
          </h2>
          {exams.length === 0 ? (
            <p className="detail-empty">Sin registros.</p>
          ) : (
            <>
              <ul className="detail-list detail-list--clickable">
                {exams.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setDetailExam({ ...e, patients: patient })}
                    >
                      {formatDate(e.date)} {e.equipment ? `· ${e.equipment}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
              {exams.length === HISTORY_LIMIT && (
                <p className="text-muted">Mostrando las últimas {HISTORY_LIMIT}.</p>
              )}
            </>
          )}
        </section>

        <section className="detail-section">
          <h2>
            <span className="detail-section__icon" aria-hidden="true">🎧</span> Audífonos vendidos
          </h2>
          {hearingaids.length === 0 ? (
            <p className="detail-empty">Sin registros.</p>
          ) : (
            <>
              <ul className="detail-list">
                {hearingaids.map((h) => (
                  <li key={h.id}>
                    {formatDate(h.purchase_date)} · {h.brand} {h.model} ({h.side})
                    {h.price != null ? ` — ${formatCurrency(h.price)}` : ''}
                  </li>
                ))}
              </ul>
              {hearingaids.length === HISTORY_LIMIT && (
                <p className="text-muted">Mostrando los últimos {HISTORY_LIMIT}.</p>
              )}
            </>
          )}
        </section>

        <section className="detail-section">
          <h2>
            <span className="detail-section__icon" aria-hidden="true">👓</span> Recetas de lentes
          </h2>
          {prescriptions.length === 0 ? (
            <p className="detail-empty">Sin registros.</p>
          ) : (
            <>
              <ul className="detail-list detail-list--clickable">
                {prescriptions.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setDetailPrescription({ ...p, patients: patient })}
                    >
                      {formatDate(p.date)} {p.uso ? `· ${p.uso}` : ''}
                      {p.diagnostico?.length ? ` · ${p.diagnostico.join(', ')}` : ''}
                      {p.reevaluar ? ' · Reevaluar' : ''}
                    </button>
                  </li>
                ))}
              </ul>
              {prescriptions.length === HISTORY_LIMIT && (
                <p className="text-muted">Mostrando las últimas {HISTORY_LIMIT}.</p>
              )}
            </>
          )}
        </section>

        <section className="detail-section">
          <h2>
            <span className="detail-section__icon" aria-hidden="true">🛍️</span> Lentes comprados
          </h2>
          {oftsales.length === 0 ? (
            <p className="detail-empty">Sin registros.</p>
          ) : (
            <>
              <ul className="detail-list">
                {oftsales.map((o) => (
                  <li key={o.id}>
                    {formatDate(o.date)} · {o.compro ? 'Compró' : 'No compró'}
                    {o.compro ? ` — ${formatCurrency(o.price)}` : ''}
                  </li>
                ))}
              </ul>
              {oftsales.length === HISTORY_LIMIT && (
                <p className="text-muted">Mostrando las últimas {HISTORY_LIMIT}.</p>
              )}
            </>
          )}
        </section>
      </div>

      <PatientAttachments patientId={patient.id} actorId={professional.id} />

      {editing && (
        <PatientFormModal
          patient={patient}
          branches={selectableBranches}
          defaultBranchId={patient.branch_ids?.[0]}
          actorId={professional.id}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
        />
      )}

      {detailExam && (
        <ExamDetailModal
          exam={detailExam}
          activeBranchId={activeBranchId}
          onClose={() => setDetailExam(null)}
          onEdit={() => navigate('/app/audiometrias')}
        />
      )}

      {detailPrescription && (
        <PrescriptionDetailModal
          prescription={detailPrescription}
          onClose={() => setDetailPrescription(null)}
          onEdit={() => navigate('/app/recetas')}
          onPrint={async () => {
            const [logoUrl, signatureUrl] = await Promise.all([
              getPatientBranchLogoUrl(detailPrescription.patients, activeBranchId),
              getProfessionalSignatureUrl(detailPrescription.professionals),
            ]);
            printPrescriptionReport(detailPrescription, logoUrl, signatureUrl);
          }}
        />
      )}
    </div>
  );
}
